import { Camera, ChevronLeft, Minus, Pencil, Plus } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { splitMemberFullName } from '../api/membersDirectory';
import { AddChildModal } from './AddChildModal';
import { EditProfileModal } from './EditProfileModal';
import { EditSpouseModal } from './EditSpouseModal';
import { MemberAvatarOrPhoto } from './MemberCard';

const dateDisplay = new Intl.DateTimeFormat('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
});

export const EMPTY_FIELD = '—';

const HERO_AVATAR_SKELETON = 116;

export function MemberDetailPageSkeleton() {
    return (
        <div role="status" aria-live="polite" aria-busy="true">
            <span className="visually-hidden">
                Üye bilgileri yükleniyor…
            </span>
            <SkeletonTheme
                baseColor="#e8ecf2"
                highlightColor="#f0f3f8"
            >
                <nav
                    className="member-detail-nav mb-4 member-detail-skeleton-block"
                    aria-hidden
                >
                    <Skeleton height={22} width={160} borderRadius={6} />
                </nav>

                <header className="member-detail-hero rounded-4 mb-4 member-detail-skeleton-hero">
                    <div className="member-detail-hero-photo-wrap">
                        <Skeleton
                            width={HERO_AVATAR_SKELETON}
                            height={HERO_AVATAR_SKELETON}
                            borderRadius={12}
                        />
                    </div>
                    <div className="member-detail-hero-text flex-grow-1 min-w-0">
                        <Skeleton
                            height={34}
                            width="min(100%, 280px)"
                            className="mb-3"
                            borderRadius={8}
                        />
                        <Skeleton
                            height={16}
                            width="min(100%, 180px)"
                            className="mb-2"
                            borderRadius={6}
                        />
                        <Skeleton
                            height={14}
                            width="min(100%, 140px)"
                            borderRadius={6}
                        />
                    </div>
                </header>

                <div className="row g-4">
                    {[0, 1, 2, 3].map((i) => (
                        <div key={i} className="col-lg-6">
                            <section
                                className="member-detail-panel rounded-4 p-4 h-100 member-detail-skeleton-panel"
                                aria-hidden
                            >
                                <Skeleton
                                    height={20}
                                    width={120}
                                    className="mb-3"
                                    borderRadius={6}
                                />
                                <Skeleton height={14} className="mb-3" />
                                <Skeleton height={14} className="mb-3" />
                                <Skeleton height={14} className="mb-3" />
                                <Skeleton height={14} width="85%" />
                            </section>
                        </div>
                    ))}
                </div>
            </SkeletonTheme>
        </div>
    );
}

/**
 * @param {string | null | undefined} iso
 */
function formatIsoDate(iso) {
    if (!iso || typeof iso !== 'string') return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return dateDisplay.format(d);
}

/**
 * @param {string | null | undefined} val
 */
function displayOrDash(val) {
    if (val == null) return EMPTY_FIELD;
    const t = String(val).trim();
    return t ? t : EMPTY_FIELD;
}

/**
 * @param {string | null | undefined} raw
 */
function normalizeExternalUrl(raw) {
    const t = String(raw || '').trim();
    if (!t) return '';
    if (/^https?:\/\//i.test(t)) return t;
    return `https://${t}`;
}

/** @type {Record<number, string>} */
const MEMBERSHIP_TYPE_I18N_KEYS = {
    0: 'memberDetailPage.membershipType.regular',
    1: 'memberDetailPage.membershipType.attendanceExempt',
    2: 'memberDetailPage.membershipType.duesExempt',
    3: 'memberDetailPage.membershipType.honorary',
    4: 'memberDetailPage.membershipType.onLeave',
};

/**
 * @param {number | null} n
 * @param {(key: string) => string} t
 */
function membershipTypeLabel(n, t) {
    if (n == null) return null;
    const key = MEMBERSHIP_TYPE_I18N_KEYS[n];
    return key ? t(key) : null;
}

/**
 * @param {import('../api/membersDirectory').mapApiMemberDetailToView extends (...args: unknown[]) => infer R ? R : never} member
 * @param {{
 *   backLink?: { to: string; label: string } | null;
 *   photoEditable?: boolean;
 *   photoUploading?: boolean;
 *   onPhotoSelected?: (file: File) => void;
 *   childrenEditable?: boolean;
 *   childSubmitting?: boolean;
 *   onAddChild?: (payload: {
 *     firstName: string;
 *     lastName: string;
 *     birthDate: string;
 *   }) => void | Promise<void>;
 *   onDeleteChild?: (childId: string) => void | Promise<void>;
 *   onUpdateChild?: (payload: {
 *     id: string;
 *     firstName: string;
 *     lastName: string;
 *     birthDate: string;
 *   }) => void | Promise<void>;
 *   deletingChildId?: string | null;
 *   spouseEditable?: boolean;
 *   spouseSubmitting?: boolean;
 *   onUpdateSpouse?: (
 *     subMemberId: string,
 *     payload: Record<string, string>,
 *   ) => void | Promise<void>;
 *   profileEditable?: boolean;
 *   profileSubmitting?: boolean;
 *   onUpdateProfile?: (
 *     payload: Record<string, string>,
 *   ) => void | Promise<void>;
 *   spouseLinkFromProfile?: boolean;
 *   memberLinkState?: { fromProfile?: boolean, fromFellowshipGroups?: boolean };
 * }} [props]
 */
export function MemberDetailContent({
    member,
    backLink = null,
    photoEditable = false,
    photoUploading = false,
    onPhotoSelected,
    childrenEditable = false,
    childSubmitting = false,
    onAddChild,
    onDeleteChild,
    onUpdateChild,
    deletingChildId = null,
    spouseEditable = false,
    spouseSubmitting = false,
    onUpdateSpouse,
    profileEditable = false,
    profileSubmitting = false,
    onUpdateProfile,
    spouseLinkFromProfile = false,
    memberLinkState = undefined,
}) {
    const { t } = useTranslation();
    const photoInputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
    const [childModalOpen, setChildModalOpen] = useState(false);
    const [editingChild, setEditingChild] = useState(
        /** @type {null | { id: string; firstName: string; lastName: string; birthDate: string | null }} */ (
            null
        ),
    );
    const [spouseModalOpen, setSpouseModalOpen] = useState(false);
    const [editingSpouse, setEditingSpouse] = useState(
        /** @type {null | import('../api/membersDirectory').mapApiSpouseToView extends (...args: unknown[]) => infer R ? R : never} */ (
            null
        ),
    );
    const [profileModalOpen, setProfileModalOpen] = useState(false);
    const m = member;
    const { givenName, familyName } = splitMemberFullName(m.fullName);
    const spouses = Array.isArray(m.spouses) ? m.spouses : [];
    const children = Array.isArray(m.children) ? m.children : [];
    const showRelatedMember = Boolean(m.referenceMemberId?.trim());
    const isSubMemberProfile = m.isPartnerProfile === true;

    function openAddChildModal() {
        setEditingChild(null);
        setChildModalOpen(true);
    }

    function openEditChildModal(row) {
        const childId = String(row.id || '').trim();
        if (!childId) return;

        let firstName = String(row.firstName || '').trim();
        let lastName = String(row.lastName || '').trim();
        if (!firstName && !lastName) {
            const split = splitMemberFullName(String(row.fullName || ''));
            firstName = split.givenName;
            lastName = split.familyName;
        }

        setEditingChild({
            id: childId,
            firstName,
            lastName,
            birthDate: row.birthDate ? String(row.birthDate) : null,
        });
        setChildModalOpen(true);
    }

    async function handleChildModalSubmit(payload) {
        if (payload.id) {
            if (!onUpdateChild) return;
            await onUpdateChild({
                id: payload.id,
                firstName: payload.firstName,
                lastName: payload.lastName,
                birthDate: payload.birthDate,
            });
        } else {
            if (!onAddChild) return;
            await onAddChild(payload);
        }
        setChildModalOpen(false);
        setEditingChild(null);
    }

    function closeChildModal() {
        setChildModalOpen(false);
        setEditingChild(null);
    }

    function openEditSpouseModal(spouse) {
        if (!String(spouse?.id || '').trim()) return;
        setEditingSpouse(spouse);
        setSpouseModalOpen(true);
    }

    function closeSpouseModal() {
        setSpouseModalOpen(false);
        setEditingSpouse(null);
    }

    async function handleSpouseModalSubmit(subMemberId, payload) {
        if (!onUpdateSpouse) return;
        await onUpdateSpouse(subMemberId, payload);
        closeSpouseModal();
    }

    async function handleProfileModalSubmit(payload) {
        if (!onUpdateProfile) return;
        await onUpdateProfile(payload);
        setProfileModalOpen(false);
    }

    function handlePhotoInputChange(e) {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (file && onPhotoSelected) {
            onPhotoSelected(file);
        }
    }

    return (
        <>
            {backLink ? (
                <nav className="member-detail-nav mb-4" aria-label="Üye detayı">
                    <Link
                        to={backLink.to}
                        className="member-detail-back-link d-inline-flex align-items-center gap-2 text-decoration-none"
                    >
                        <ChevronLeft size={20} strokeWidth={2} aria-hidden />
                        {backLink.label}
                    </Link>
                </nav>
            ) : null}

            <header className="member-detail-hero rounded-4 mb-4">
                {profileEditable ? (
                    <button
                        type="button"
                        className="member-detail-hero-edit-btn btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                        onClick={() => setProfileModalOpen(true)}
                        disabled={profileSubmitting}
                    >
                        <Pencil size={16} strokeWidth={2} aria-hidden />
                        {t('profilePage.editProfileButton')}
                    </button>
                ) : null}
                <div
                    className={`member-detail-hero-photo-wrap${photoEditable ? ' member-detail-hero-photo-wrap--editable' : ''}`}
                >
                    <div className="member-detail-hero-photo-inner">
                        <MemberAvatarOrPhoto
                            key={m.profilePictureUrl || 'no-photo'}
                            imageUrl={m.profilePictureUrl}
                            givenName={givenName}
                            familyName={familyName}
                            className="member-detail-hero-avatar"
                        />
                        {photoEditable ? (
                            <>
                                <input
                                    ref={photoInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="visually-hidden"
                                    onChange={handlePhotoInputChange}
                                    disabled={photoUploading}
                                />
                                <button
                                    type="button"
                                    className="member-detail-photo-edit-btn"
                                    aria-label={t(
                                        'profilePage.changePhotoAria',
                                    )}
                                    title={t('profilePage.changePhoto')}
                                    disabled={photoUploading}
                                    onClick={() =>
                                        photoInputRef.current?.click()
                                    }
                                >
                                    <Camera
                                        size={16}
                                        strokeWidth={2}
                                        aria-hidden
                                    />
                                </button>
                            </>
                        ) : null}
                    </div>
                </div>
                <div className="member-detail-hero-text">
                    <h1 className="member-detail-name mb-2">
                        {displayOrDash(m.fullName)}
                    </h1>
                    <p className="member-detail-membership-type mb-0 text-secondary small">
                        {membershipTypeLabel(m.membershipType, t) ??
                            EMPTY_FIELD}
                    </p>
                </div>
            </header>

            <div className="row g-4">
                <div className="col-lg-6">
                    <section
                        className="member-detail-panel rounded-4 p-4 h-100"
                        aria-labelledby="member-detail-contact-title"
                    >
                        <h2
                            id="member-detail-contact-title"
                            className="member-detail-section-title h6 mb-3"
                        >
                            İletişim
                        </h2>
                        <dl className="member-detail-dl mb-0">
                            <div className="member-detail-dl-item">
                                <dt>E-posta</dt>
                                <dd>
                                    {m.email?.trim() ? (
                                        <a
                                            href={`mailto:${m.email.trim()}`}
                                            className="member-detail-link"
                                        >
                                            {m.email.trim()}
                                        </a>
                                    ) : (
                                        EMPTY_FIELD
                                    )}
                                </dd>
                            </div>
                            <div className="member-detail-dl-item">
                                <dt>Telefon</dt>
                                <dd>
                                    {m.phoneNumber?.trim() ? (
                                        <a
                                            href={`tel:${m.phoneNumber.replace(/\s/g, '')}`}
                                            className="member-detail-link"
                                        >
                                            {m.phoneNumber}
                                        </a>
                                    ) : (
                                        EMPTY_FIELD
                                    )}
                                </dd>
                            </div>
                            {!isSubMemberProfile ? (
                                <div className="member-detail-dl-item">
                                    <dt>Web sitesi</dt>
                                    <dd>
                                        {m.website?.trim() ? (
                                            <a
                                                href={normalizeExternalUrl(
                                                    m.website,
                                                )}
                                                className="member-detail-link"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {m.website}
                                            </a>
                                        ) : (
                                            EMPTY_FIELD
                                        )}
                                    </dd>
                                </div>
                            ) : null}
                            <div className="member-detail-dl-item">
                                <dt>Adres</dt>
                                <dd>{displayOrDash(m.address)}</dd>
                            </div>
                        </dl>
                    </section>
                </div>

                <div className="col-lg-6">
                    <section
                        className="member-detail-panel rounded-4 p-4 h-100"
                        aria-labelledby="member-detail-work-title"
                    >
                        <h2
                            id="member-detail-work-title"
                            className="member-detail-section-title h6 mb-3"
                        >
                            İş ve sektör
                        </h2>
                        <dl className="member-detail-dl mb-0">
                            <div className="member-detail-dl-item">
                                <dt>Ünvan</dt>
                                <dd>{displayOrDash(m.jobTitle)}</dd>
                            </div>
                            <div className="member-detail-dl-item">
                                <dt>Şirket</dt>
                                <dd>{displayOrDash(m.company)}</dd>
                            </div>
                            <div className="member-detail-dl-item">
                                <dt>Sektör</dt>
                                <dd>{displayOrDash(m.sector)}</dd>
                            </div>
                            {!isSubMemberProfile ? (
                                <div className="member-detail-dl-item">
                                    <dt>İş adresi</dt>
                                    <dd>{displayOrDash(m.workAddress)}</dd>
                                </div>
                            ) : null}
                        </dl>
                    </section>
                </div>

                <div className="col-lg-6">
                    <section
                        className="member-detail-panel rounded-4 p-4 h-100"
                        aria-labelledby="member-detail-personal-title"
                    >
                        <h2
                            id="member-detail-personal-title"
                            className="member-detail-section-title h6 mb-3"
                        >
                            Kişisel
                        </h2>
                        <dl className="member-detail-dl mb-0">
                            <div className="member-detail-dl-item">
                                <dt>Doğum tarihi</dt>
                                <dd>
                                    {formatIsoDate(m.birthDate) ? (
                                        <time
                                            dateTime={m.birthDate || undefined}
                                        >
                                            {formatIsoDate(m.birthDate)}
                                        </time>
                                    ) : (
                                        EMPTY_FIELD
                                    )}
                                </dd>
                            </div>
                            {!isSubMemberProfile ? (
                                <div className="member-detail-dl-item">
                                    <dt>Kulübe giriş</dt>
                                    <dd>
                                        {formatIsoDate(m.clubJoinDate) ? (
                                            <time
                                                dateTime={
                                                    m.clubJoinDate ||
                                                    undefined
                                                }
                                            >
                                                {formatIsoDate(
                                                    m.clubJoinDate,
                                                )}
                                            </time>
                                        ) : (
                                            EMPTY_FIELD
                                        )}
                                    </dd>
                                </div>
                            ) : null}
                            <div className="member-detail-dl-item">
                                <dt>Üniversite</dt>
                                <dd>{displayOrDash(m.university)}</dd>
                            </div>
                            <div className="member-detail-dl-item">
                                <dt>Lise</dt>
                                <dd>{displayOrDash(m.highSchool)}</dd>
                            </div>
                            {!isSubMemberProfile ? (
                                <div className="member-detail-dl-item">
                                    <dt>İlgi alanları</dt>
                                    <dd>{displayOrDash(m.hobbies)}</dd>
                                </div>
                            ) : null}
                        </dl>
                    </section>
                </div>

                {!isSubMemberProfile ? (
                <div className="col-lg-6">
                    <section
                        className="member-detail-panel rounded-4 p-4 h-100"
                        aria-labelledby="member-detail-social-title"
                    >
                        <h2
                            id="member-detail-social-title"
                            className="member-detail-section-title h6 mb-3"
                        >
                            Sosyal medya
                        </h2>
                        <dl className="member-detail-dl mb-0">
                            <div className="member-detail-dl-item">
                                <dt>Facebook</dt>
                                <dd>
                                    {m.facebook?.trim() ? (
                                        <a
                                            href={normalizeExternalUrl(
                                                m.facebook,
                                            )}
                                            className="member-detail-link"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {m.facebook.trim()}
                                        </a>
                                    ) : (
                                        EMPTY_FIELD
                                    )}
                                </dd>
                            </div>
                            <div className="member-detail-dl-item">
                                <dt>X (Twitter)</dt>
                                <dd>
                                    {m.twitter?.trim() ? (
                                        <a
                                            href={normalizeExternalUrl(
                                                m.twitter,
                                            )}
                                            className="member-detail-link"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {m.twitter.trim()}
                                        </a>
                                    ) : (
                                        EMPTY_FIELD
                                    )}
                                </dd>
                            </div>
                            <div className="member-detail-dl-item">
                                <dt>LinkedIn</dt>
                                <dd>
                                    {m.linkedIn?.trim() ? (
                                        <a
                                            href={normalizeExternalUrl(
                                                m.linkedIn,
                                            )}
                                            className="member-detail-link"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {m.linkedIn.trim()}
                                        </a>
                                    ) : (
                                        EMPTY_FIELD
                                    )}
                                </dd>
                            </div>
                        </dl>
                    </section>
                </div>
                ) : null}
            </div>

            <section
                className="member-detail-panel rounded-4 p-4 mt-4"
                aria-labelledby="member-detail-partner-title"
            >
                <div className="member-detail-children-head d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                    <h2
                        id="member-detail-partner-title"
                        className="member-detail-section-title h6 mb-0"
                    >
                        Eş
                    </h2>
                    {spouseEditable &&
                    spouses.length > 0 &&
                    spouses.some((s) => String(s.id || '').trim()) ? (
                        <button
                            type="button"
                            className="member-detail-add-child-btn btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                            onClick={() =>
                                openEditSpouseModal(
                                    spouses.find((s) =>
                                        String(s.id || '').trim(),
                                    ) || spouses[0],
                                )
                            }
                        >
                            <Pencil size={16} strokeWidth={2} aria-hidden />
                            {t('profilePage.editSpouseButton')}
                        </button>
                    ) : null}
                </div>
                {spouses.length > 0 ? (
                    <ul className="member-detail-spouse-list list-unstyled row row-cols-1 row-cols-sm-2 g-3 mb-0">
                        {spouses.map((spouse, idx) => {
                            const subSplit = splitMemberFullName(
                                spouse.fullName,
                            );
                            const card = (
                                <div className="member-detail-spouse-card d-flex align-items-center gap-3 p-3 rounded-3">
                                    <MemberAvatarOrPhoto
                                        imageUrl={spouse.profilePictureUrl}
                                        givenName={subSplit.givenName}
                                        familyName={subSplit.familyName}
                                        className="member-avatar--partner member-detail-partner-avatar"
                                    />
                                    <span className="member-detail-partner-name">
                                        {displayOrDash(spouse.fullName)}
                                    </span>
                                </div>
                            );
                            return (
                                <li
                                    key={spouse.id || `spouse-${idx}`}
                                    className="col"
                                >
                                    {spouse.id ? (
                                        <Link
                                            to={`/member-area/members/${encodeURIComponent(spouse.id)}`}
                                            state={
                                                spouseLinkFromProfile
                                                    ? { fromProfile: true }
                                                    : memberLinkState
                                            }
                                            className="text-decoration-none text-reset d-block"
                                        >
                                            {card}
                                        </Link>
                                    ) : (
                                        card
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <p className="member-detail-empty-line mb-0 text-secondary">
                        {EMPTY_FIELD}
                    </p>
                )}
            </section>

            {showRelatedMember ? (
                <section
                    className="member-detail-panel rounded-4 p-4 mt-4"
                    aria-labelledby="member-detail-related-title"
                >
                    <h2
                        id="member-detail-related-title"
                        className="member-detail-section-title h6 mb-3"
                    >
                        İlişkili üyeler
                    </h2>
                    <p className="mb-0">
                        <Link
                            to={`/member-area/members/${encodeURIComponent(m.referenceMemberId)}`}
                            state={memberLinkState}
                            className="member-detail-link fw-semibold"
                        >
                            İlişkili üye profilini görüntüle
                        </Link>
                    </p>
                </section>
            ) : null}

            <section
                className="member-detail-panel rounded-4 p-4 mt-4"
                aria-labelledby="member-detail-children-title"
            >
                <div className="member-detail-children-head d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                    <h2
                        id="member-detail-children-title"
                        className="member-detail-section-title h6 mb-0"
                    >
                        Çocuklar
                    </h2>
                    {childrenEditable ? (
                        <button
                            type="button"
                            className="member-detail-add-child-btn btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                            onClick={openAddChildModal}
                        >
                            <Plus size={16} strokeWidth={2} aria-hidden />
                            {t('profilePage.addChildButton')}
                        </button>
                    ) : null}
                </div>
                {children.length > 0 ? (
                    <ul className="member-detail-children-list list-unstyled mb-0">
                        {children.map((row, idx) => {
                            const name = String(row.fullName || '').trim();
                            const bd = row.birthDate
                                ? formatIsoDate(String(row.birthDate))
                                : '';
                            const childId = String(row.id || '').trim();
                            return (
                                <li
                                    key={row.id || `child-${idx}`}
                                    className="member-detail-child-row py-2 border-bottom border-light"
                                >
                                    <span className="fw-semibold">
                                        {name || EMPTY_FIELD}
                                    </span>
                                    <span className="member-detail-child-meta text-secondary ms-2 small">
                                        <span className="member-detail-child-birth">
                                            ({bd || EMPTY_FIELD})
                                        </span>
                                        {childrenEditable && childId ? (
                                            <span className="member-detail-child-actions">
                                                <button
                                                    type="button"
                                                    className="member-detail-child-action-btn"
                                                    onClick={() =>
                                                        openEditChildModal(row)
                                                    }
                                                    disabled={
                                                        childSubmitting ||
                                                        deletingChildId ===
                                                            childId
                                                    }
                                                    aria-label={t(
                                                        'profilePage.editChildAria',
                                                        {
                                                            name:
                                                                name ||
                                                                EMPTY_FIELD,
                                                        },
                                                    )}
                                                    title={t(
                                                        'profilePage.editChild',
                                                    )}
                                                >
                                                    <Pencil
                                                        size={14}
                                                        strokeWidth={2}
                                                        aria-hidden
                                                    />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="member-detail-child-action-btn member-detail-child-action-btn--remove"
                                                    onClick={() =>
                                                        onDeleteChild?.(
                                                            childId,
                                                        )
                                                    }
                                                    disabled={
                                                        deletingChildId ===
                                                            childId ||
                                                        childSubmitting
                                                    }
                                                    aria-label={t(
                                                        'profilePage.removeChildAria',
                                                        {
                                                            name:
                                                                name ||
                                                                EMPTY_FIELD,
                                                        },
                                                    )}
                                                    title={t(
                                                        'profilePage.removeChild',
                                                    )}
                                                >
                                                    <Minus
                                                        size={14}
                                                        strokeWidth={2}
                                                        aria-hidden
                                                    />
                                                </button>
                                            </span>
                                        ) : null}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <p className="member-detail-empty-line mb-0 text-secondary">
                        {EMPTY_FIELD}
                    </p>
                )}
            </section>

            {childrenEditable ? (
                <AddChildModal
                    open={childModalOpen}
                    onClose={closeChildModal}
                    onSubmit={handleChildModalSubmit}
                    submitting={childSubmitting}
                    mode={editingChild ? 'edit' : 'add'}
                    initialValues={editingChild}
                />
            ) : null}

            {spouseEditable ? (
                <EditSpouseModal
                    open={spouseModalOpen}
                    onClose={closeSpouseModal}
                    onSubmit={handleSpouseModalSubmit}
                    submitting={spouseSubmitting}
                    spouse={editingSpouse}
                />
            ) : null}

            {profileEditable ? (
                <EditProfileModal
                    open={profileModalOpen}
                    onClose={() => setProfileModalOpen(false)}
                    onSubmit={handleProfileModalSubmit}
                    submitting={profileSubmitting}
                    member={m}
                />
            ) : null}
        </>
    );
}
